<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { useRoute } from 'vue-router'

    import { dynamicUiRoutes } from '@common/dynamicUiRoutes'

    import type { ProjectUserDeleterViewModel } from '@ProjectUsers/Components/ProjectUserDeleter'
    import type { DependentEntityInfo } from '@common/types'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ProjectUserDeleterViewModel
    }>()

    const emit = defineEmits<{
        (e: 'delete-project-user', projectUserId: number): void
        (e: 'archive-project-user', projectUserId: number): void
    }>()

    // ---------------- router ----------------

    const route = useRoute()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const projectUserId = computed(() => vm.value.projectUserId)
    const projectUserName = computed(() => vm.value.projectUserName)

    const initialized = computed(() => vm.value.deps != null)

    const hasDependants = computed(() => vm.value.deps?.HasDependants ?? false)
    const dependenciesCount = computed(() => vm.value.deps?.DependenciesCount ?? 0)

    const estimationsSurveys = computed(() => vm.value.deps?.EstimationsSurveys ?? [])
    const criteriaSurveys = computed(() => vm.value.deps?.CriteriaSurveys ?? [])
    const scoringSurveys = computed(() => vm.value.deps?.ScoringSurveys ?? [])

    // ---------------- url helpers ----------------

    function url_CriteriaPanelistsPage(s: DependentEntityInfo): string
    {
        return dynamicUiRoutes.Evaluation_CriteriaPanelistsPage(
            route,
            s.WorkspaceId,
            s.TchProjectId,
            s.EvalModelId,
            Number.parseInt(s.Id)
        )
    }

    function url_ScoringPanelistsPage(s: DependentEntityInfo): string
    {
        return dynamicUiRoutes.Evaluation_ScoringPanelistsPage(
            route,
            s.WorkspaceId,
            s.TchProjectId,
            s.EvalModelId,
            Number.parseInt(s.Id)
        )
    }

    function url_AssessmentsPanelistsPage(s: DependentEntityInfo): string
    {
        return dynamicUiRoutes.Evaluation_AssessmentsPanelistsPage(
            route,
            s.WorkspaceId,
            s.TchProjectId,
            Number.parseInt(s.Id)
        )
    }

    // ---------------- actions ----------------

    function deleteProjectUser()
    {
        emit('delete-project-user', projectUserId.value)
    }

    function archiveProjectUser()
    {
        emit('archive-project-user', projectUserId.value)
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">

            <!-- loading -->
            <div v-if="!initialized" class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Delete user</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h4><i class="fa fa-spinner fa-spin"></i> Checking dependencies...</h4>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>

            <!-- has dependants -->
            <div v-if="initialized && hasDependants" class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Delete user</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="alert alert-danger text-dark">
                            <h5 class="alert-heading">Cannot delete this user</h5>
                            <p>
                                The user '<strong>{{projectUserName}}</strong>' is in
                                <strong>{{dependenciesCount}}</strong> places.
                                To delete them & their votes, first remove them from the following panels.
                                Alternatively, archive the user to keep their votes.
                            </p>
                        </div>

                        <div>
                            <div class="card" v-if="criteriaSurveys.length > 0">
                                <div class="card-header">
                                    Criteria panels ({{criteriaSurveys.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="s in criteriaSurveys" :key="s.Id">
                                        <a class="text-primary"
                                           target="_blank"
                                           v-sanitizedHref="url_CriteriaPanelistsPage(s)">
                                            {{s.Name}}
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <div class="card mt-2" v-if="estimationsSurveys.length > 0">
                                <div class="card-header">
                                    Assessments panels ({{estimationsSurveys.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="s in estimationsSurveys" :key="s.Id">
                                        <a class="text-primary"
                                           target="_blank"
                                           v-sanitizedHref="url_AssessmentsPanelistsPage(s)">
                                            {{s.Name}}
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <div class="card mt-2" v-if="scoringSurveys.length > 0">
                                <div class="card-header">
                                    Scoring panels ({{scoringSurveys.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="s in scoringSurveys" :key="s.Id">
                                        <a class="text-primary"
                                           target="_blank"
                                           v-sanitizedHref="url_ScoringPanelistsPage(s)">
                                            {{s.Name}}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="archiveProjectUser" class="btn btn-primary">Archive</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>

            <!-- no dependants -->
            <div v-if="initialized && !hasDependants" class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title">Delete user</h5>
                        <button type="button" class="close text-white" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h4>Are you sure you want to delete this user?</h4>
                        <p>
                            You are about to delete '<strong>{{projectUserName}}</strong>'.
                            This can't be undone.
                        </p>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="deleteProjectUser" class="btn btn-danger">Delete</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>
